import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { MapPin, GraduationCap, Calendar as CalendarIcon, ArrowLeft, Users, Mail, Phone, User, X, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SchoolDetail = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [school, setSchool] = useState(null);
  const [loadingSchool, setLoadingSchool] = useState(true);
  const [schoolGuides, setSchoolGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    message: ''
  });
  
  // Get the scroll position passed from ExploreSchools
  const previousScrollPosition = location.state?.fromScrollPosition || 0;

  const handleBookingChange = (e) => {
    setBookingForm({ ...bookingForm, [e.target.name]: e.target.value });
  };

  const handleBookTour = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/api/guide-bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guide_id: selectedGuide.id,
          guide_name: selectedGuide.name,
          school_name: selectedGuide.school,
          visitor_name: bookingForm.name,
          visitor_email: bookingForm.email,
          visitor_phone: bookingForm.phone,
          preferred_date: bookingForm.date,
          message: bookingForm.message
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit booking');
      }

      setBookingSuccess(true);
      setBookingForm({ name: '', email: '', phone: '', date: '', message: '' });
      
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Booking Failed",
        description: "Failed to submit your booking. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeBookingModal = () => {
    setSelectedGuide(null);
    setBookingSuccess(false);
    setBookingForm({ name: '', email: '', phone: '', date: '', message: '' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch school from API
  useEffect(() => {
    const fetchSchool = async () => {
      try {
        setLoadingSchool(true);
        const response = await fetch(`${API}/api/schools?visible_only=false`);
        if (response.ok) {
          const allSchools = await response.json();
          // Find school by ID (UUID)
          const foundSchool = allSchools.find(s => s.id === schoolId);
          setSchool(foundSchool);
        }
      } catch (error) {
        console.error('Failed to fetch school:', error);
      } finally {
        setLoadingSchool(false);
      }
    };
    fetchSchool();
  }, [schoolId]);

  // Fetch approved guides for this school
  useEffect(() => {
    const fetchGuides = async () => {
      if (!school) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${API}/api/guides/approved`);
        if (response.ok) {
          const allGuides = await response.json();
          // Filter guides that match this school (case-insensitive partial match)
          const filtered = allGuides.filter(guide => {
            const guideSchool = guide.school.toLowerCase().trim();
            const schoolName = school?.name.toLowerCase().trim() || '';
            
            // Extract key words from school names for matching
            const matchPatterns = [
              ['nyu', 'new york university'],
              ['columbia'],
              ['harvard'],
              ['mit'],
              ['boston university'],
              ['boston college'],
              ['ucla'],
              ['stanford'],
              ['berkeley', 'uc berkeley'],
              ['chicago', 'university of chicago'],
              ['northwestern'],
              ['georgetown'],
              ['the new school', 'new school'],
              ['cornell'],
              ['yale'],
              ['princeton'],
              ['brown'],
              ['dartmouth'],
              ['upenn', 'penn', 'university of pennsylvania'],
            ];
            
            // Check each pattern
            for (const patterns of matchPatterns) {
              const guideMatches = patterns.some(p => guideSchool.includes(p));
              const schoolMatches = patterns.some(p => schoolName.includes(p));
              if (guideMatches && schoolMatches) {
                return true;
              }
            }
            
            // Fallback: exact match
            return guideSchool === schoolName;
          });
          setSchoolGuides(filtered);
        }
      } catch (error) {
        console.error('Failed to fetch guides:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGuides();
  }, [school]);

  if (loadingSchool) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 pb-16 flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!school) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-3xl font-serif mb-4">School not found</h1>
          <Button onClick={() => navigate('/explore-schools')}>
            Back to Schools
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section with School Image */}
      <section 
        className="relative h-[50vh] bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${school.image}')`
        }}
      >
        <div className="text-center text-white px-4 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-serif mb-4">{school.name}</h1>
          <div className="flex items-center justify-center text-lg">
            <MapPin className="w-5 h-5 mr-2" />
            {school.city}
          </div>
        </div>
      </section>

      {/* Back Button and Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button 
          variant="outline" 
          onClick={() => navigate('/explore-schools', { state: { scrollPosition: previousScrollPosition } })}
          className="mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to All Schools
        </Button>

        {/* Available Guides Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-serif mb-6">Available Tour Guides</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading guides...</p>
            </div>
          ) : schoolGuides.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {schoolGuides.map((guide) => (
                <Card 
                  key={guide.id} 
                  className="hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                  onClick={() => navigate(`/guide/${guide.id}`)}
                >
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={guide.photo_urls && guide.photo_urls.length > 0 ? guide.photo_urls[0] : 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400'} 
                      alt={guide.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{guide.name}</CardTitle>
                    <CardDescription className="flex items-center">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      {guide.school}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">{guide.bio}</p>
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      <span className="line-clamp-1">{guide.availability}</span>
                    </div>
                    
                    {/* Photo Gallery Preview */}
                    {guide.photo_urls && guide.photo_urls.length > 1 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">More photos:</p>
                        <div className="flex gap-2">
                          {guide.photo_urls.slice(1, 4).map((url, idx) => (
                            <img 
                              key={idx}
                              src={url}
                              alt={`${guide.name} photo ${idx + 2}`}
                              className="w-12 h-12 rounded object-cover object-center"
                            />
                          ))}
                          {guide.photo_urls.length > 4 && (
                            <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                              +{guide.photo_urls.length - 4}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      className="w-full bg-black text-white hover:bg-gray-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/guide/${guide.id}`);
                      }}
                    >
                      View Profile
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No guides available yet</h3>
                <p className="text-gray-500 mb-6">Be the first to offer tours at {school.name}!</p>
                <Button 
                  onClick={() => navigate('/explore-schools', { state: { section: 'apply' } })}
                  className="bg-black text-white hover:bg-gray-800"
                >
                  Become a Guide
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* School Info */}
        <div className="bg-gray-50 rounded-lg p-8">
          <h3 className="text-2xl font-serif mb-4">About Campus Tours at {school.name}</h3>
          <p className="text-gray-600 mb-4">
            Discover {school.name} through the eyes of current students! Our student guides offer personalized campus tours that go beyond the typical admissions tour.
          </p>
          <p className="text-gray-600">
            Learn about student life, get insider tips, and explore the campus with someone who knows it best.
          </p>
        </div>
      </div>

      <Footer />

      {/* Booking Modal */}
      {selectedGuide && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            {!bookingSuccess ? (
              <>
                <div className="p-6 border-b flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Book a Tour with {selectedGuide.name}</h3>
                  <button onClick={closeBookingModal} className="text-gray-500 hover:text-gray-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleBookTour} className="p-6 space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-600">
                      <strong>Guide:</strong> {selectedGuide.name}<br />
                      <strong>School:</strong> {selectedGuide.school}<br />
                      <strong>Availability:</strong> {selectedGuide.availability}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <Input
                        id="name"
                        name="name"
                        value={bookingForm.name}
                        onChange={handleBookingChange}
                        required
                        placeholder="Your full name"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={bookingForm.email}
                        onChange={handleBookingChange}
                        required
                        placeholder="your.email@example.com"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={bookingForm.phone}
                        onChange={handleBookingChange}
                        required
                        placeholder="(555) 123-4567"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="date">Preferred Date *</Label>
                    <div className="relative mt-1">
                      <CalendarIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        value={bookingForm.date}
                        onChange={handleBookingChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message (Optional)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={bookingForm.message}
                      onChange={handleBookingChange}
                      rows={3}
                      placeholder="Any questions or special requests?"
                      className="mt-1"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-black text-white hover:bg-gray-800 py-6"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CalendarIcon className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-2xl font-semibold mb-2">Booking Submitted!</h3>
                <p className="text-gray-600 mb-6">
                  Your booking request is <strong>pending</strong>.<br />
                  Please wait until it's confirmed.<br />
                  We will contact you soon!
                </p>
                <Button 
                  onClick={closeBookingModal}
                  className="bg-black text-white hover:bg-gray-800"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolDetail;
